import { UserAvatarData } from "./user";

// 1. Individual File/Asset items (Documents, Photos, Videos)
export interface MediaResource {
  Id: string;
  Name: string;
  Type: 'Document' | 'Photo' | 'Video';
  Url: string;
  FileExtension: string;
  ThumbnailUrl: string | null;
  Description?: string;
  Timestamp?: string;
  DocumentUrl?: string;
  ImageUrl?: string;
  Caption?: string;
  VideoSource?: string;
  VideoUrl?: string;
  YoutubeVideoId?: string;
  YoutubeTitle?: string;
  Captions?: string | null;
}

// 2. Nested subfolders that live inside folders or containers
export interface MediaFolder {
  Id: string;
  Name: string;
  Type: 'Folder';
  FolderType: LibraryFolderType;
  Timestamp: string;
  ResourceCount: number;
  PreviewUrls: string[];
  Resources: MediaResource[];
  SubFolders: MediaFolder[];
}

// 3. Top-level content category blocks (e.g., Documents, Photos, Videos tabs)
export interface MediaContainer {
  Id: string;
  Name: string; // "Documents", "Photos", "Videos"
  FolderType: LibraryFolderType;
  Resources: MediaResource[];
  Folders: MediaFolder[];
}

// 4. The raw root JSON structure coming from your backend API
export interface SchoolLibraryResponse {
  Id: string;
  Name: string;              // "Curro Kathu High School"
  Code: string;              // "CKA"
  CampusCode: string;
  ImageUrl: string;          // "/media/iemh0qkm/curro-kathu-blue.jpg"
  LibraryLogoUrl: string | null;
  Containers: MediaContainer[];
}

// 5. The processed UI model used directly by your Library Component view layout
export interface SchoolLibrary {
  name: string;              // Maps to payload.Name
  code: string;              // Maps to payload.Code
  image: string;             // Maps to payload.ImageUrl
  children: UserAvatarData[]; // Linked local student avatars grouped by code
  containers: MediaContainer[]; // The asset packages for Screen 2
}

export interface LibraryRequestPayload {
  schoolCode: string;
  CampusCode?: string;
}


export interface PreviewResponse {
  Containers: MediaContainer[];
  Name?: string;
  Code?: string;
  ImageUrl?: string;
}

export interface SearchQueryConfig {
  schoolCode: string;
  campusCode: string;
  searchString: string;
  mediaTypes: string[];
  offset: number;
  limit: number;
}

export interface PreviewQueryConfig {
  schoolCode: string;
  campusCode?: string;
  token: string;
  offset: number;
  limit: number;
}

export interface SearchItemNormalized {
  Id: string;
  Name: string;
  IsFolder: boolean;
  ResourceCount: number;
  CoverUrl: string | null;
  Url?: string;
  FileExtension?: string | null;
  RawPayload: MediaResource | MediaFolder | null;
}

export type SearchGroupsNormalized = Record<LibraryMediaGroup, SearchItemNormalized[]>;

export interface SearchPaging {
  TotalResults: number;
  Offset: number;
  Limit: number;
  HasMore: boolean;
}

export interface SearchResultsNormalized {
  groups: SearchGroupsNormalized;
  paging: SearchPaging;
}

export interface MediaSearchResponse {
  Id?: string;
  Name?: string;
  Code?: string;
  CampusCode?: string;
  ImageUrl?: string;
  LibraryLogoUrl?: string | null;
  Containers: MediaContainer[];
  Paging?: Partial<SearchPaging>;
}

/**
 * Folder/tab categories as they exist in the library payload and navigation.
 * Note the surface term "photos" — the search side calls the same content "gallery".
 */
export enum LibraryFolderType {
  Documents = 'documents',
  Photos = 'photos',
  Videos = 'videos',
}

/**
 * Search result groups / filter chips. Mirrors LibraryFolderType except that photos
 * are surfaced as "gallery". Bridge the two via resolveGroupKey in LibraryService.
 */
export enum LibraryMediaGroup {
  Documents = 'documents',
  Videos = 'videos',
  Gallery = 'gallery',
}

export interface PreviewQueryConfig {
  schoolCode: string;
  campusCode?: string;
  token: string;
  offset: number;
  limit: number;
}
